import {IssueMarkdownViewer} from '@github-ui/commenting/IssueMarkdownViewer'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {ReactionViewer} from '@github-ui/reaction-viewer/ReactionViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {AddSubIssueButtonGroup} from '@github-ui/sub-issues/AddSubIssueButtonGroup'
import {Box} from '@primer/react'
import {type RefObject, useCallback} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {commitUpdateIssueBodyMutation} from '../mutations/update-issue-body-mutation'
import type {IssueBodyViewer$key} from './__generated__/IssueBodyViewer.graphql'
import type {IssueBodyViewerReactable$key} from './__generated__/IssueBodyViewerReactable.graphql'
import type {IssueBodyViewerSubIssues$key} from './__generated__/IssueBodyViewerSubIssues.graphql'
import {AddTasklistButton} from './AddTasklistButton'

export type IssueBodyViewerProps = {
  html: SafeHTMLString
  markdown: string
  comment: IssueBodyViewer$key
  onLinkClick?: (event: MouseEvent) => void
  viewerRef?: RefObject<HTMLDivElement>
  issueBodyRef?: React.RefObject<HTMLDivElement>
  bodyVersion: string
  viewerCanUpdate: boolean
  locked: boolean
  reactable: IssueBodyViewerReactable$key
  subIssues?: IssueBodyViewerSubIssues$key | null
}

export function IssueBodyViewer({
  html,
  markdown,
  comment,
  onLinkClick,
  viewerRef,
  issueBodyRef,
  bodyVersion,
  locked,
  viewerCanUpdate,
  reactable,
  subIssues = null,
}: IssueBodyViewerProps) {
  const {tasklist_block, sub_issues} = useFeatureFlags()
  const environment = useRelayEnvironment()
  const data = useFragment(
    graphql`
      fragment IssueBodyViewer on Comment {
        id
      }
    `,
    comment,
  )
  const dataReaction = useFragment(
    graphql`
      fragment IssueBodyViewerReactable on Reactable {
        ...ReactionViewerGroups
      }
    `,
    reactable,
  )
  const dataSubIssues = useFragment(
    graphql`
      fragment IssueBodyViewerSubIssues on Issue {
        subIssues {
          totalCount
        }
        ...AddSubIssueButtonGroup
      }
    `,
    sub_issues ? subIssues : null,
  )

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueBodyMutation({
        environment,
        input: {issueId: data.id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, environment, data.id],
  )
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 4, p: 3}} ref={issueBodyRef}>
      <IssueMarkdownViewer
        html={html}
        markdown={markdown}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSave}
        onLinkClick={onLinkClick}
      />
      <Box sx={{flexDirection: 'row', display: 'flex', alignItems: 'left'}}>
        {/* Only render the tasklist button if sub-issues are disabled and tasklists are enabled */}
        {tasklist_block && !sub_issues && viewerRef && viewerCanUpdate && <AddTasklistButton bodyRef={viewerRef} />}
        <ReactionViewer subjectId={data.id} reactionGroups={dataReaction} locked={locked} />
        {sub_issues && viewerCanUpdate && dataSubIssues && dataSubIssues.subIssues.totalCount === 0 && (
          <Box sx={{ml: 2}}>
            <AddSubIssueButtonGroup issue={dataSubIssues} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

try{ IssueBodyViewer.displayName ||= 'IssueBodyViewer' } catch {}